<template>
  <v-card class="pa-4 bg-transparent" elevation="0">
    <!-- Header informativo -->
    <div class="mb-6">
      <p class="text-subtitle-2 text--secondary mb-0">
        Selecciona una temporada de origen para cargar las tarifas existentes y elige hacia qué temporada deseas
        copiarlas.
      </p>
    </div>

    <!-- Selectores de Temporada -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4" style="border-radius: 12px; background-color: #f8fafc; border-color: #e2e8f0;">
          <h4 class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon color="primary" class="mr-2">mdi-upload</v-icon> 1. Temporada Origen
          </h4>
          <v-select v-model="temporadaOrigen" :items="temporadasList" item-text="NombreCompleto" item-value="id"
            label="Selecciona de dónde copiar" outlined dense hide-details background-color="white"
            @change="onOrigenChange"></v-select>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4" style="border-radius: 12px; background-color: #f8fafc; border-color: #e2e8f0;">
          <h4 class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon color="success" class="mr-2">mdi-download</v-icon> 2. Temporada Destino
          </h4>
          <v-select v-model="temporadaDestino" :items="temporadasList" item-text="NombreCompleto" item-value="id"
            label="Selecciona a dónde copiar" outlined dense hide-details background-color="white"></v-select>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <!-- Lista de Tarifarios a replicar -->
    <div v-if="temporadaOrigen">
      <div class="d-flex justify-space-between align-center mb-4">
        <h4 class="text-h6 font-weight-bold">3. Selecciona las tarifas a replicar</h4>
        <v-btn text color="primary" @click="toggleSelectAll" class="text-none">
          {{ selectAll ? 'Desmarcar todo' : 'Seleccionar todo' }}
        </v-btn>
      </div>

      <v-card outlined
        style="border-radius: 12px; border-color: #e2e8f0; overflow: hidden; max-height: 400px; overflow-y: auto;">
        <!-- Lista Mockup -->
        <v-list>
          <template v-for="(tarifario, index) in mockTarifarios">
            <v-list-group :key="tarifario.id" prepend-icon="mdi-folder-outline" no-action value="true">
              <template v-slot:activator>
                <v-list-item-action class="mr-3" @click.stop>
                  <v-checkbox :input-value="isTarifarioSelected(tarifario)"
                    :indeterminate="isTarifarioIndeterminate(tarifario)"
                    @change="toggleTarifario(tarifario)"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content class="mr-2">
                  <v-list-item-title class="font-weight-bold">{{ tarifario.nombre }}</v-list-item-title>
                  <v-list-item-subtitle>{{ tarifario.tarifas.length }} tarifas configuradas</v-list-item-subtitle>
                </v-list-item-content>
              </template>

              <v-list-item v-for="tarifa in tarifario.tarifas" :key="tarifa.id" class="d-flex flex-column align-stretch px-0">
                <div class="d-flex w-100 align-center px-4">
                  <v-list-item-action class="mr-3">
                    <v-checkbox v-model="selectedTarifas" :value="tarifa.id"></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2 font-weight-medium">{{ tarifa.nombre }}</v-list-item-title>
                  </v-list-item-content>
                </div>
                
                <!-- Lista de modalidades y horarios internos -->
                <div class="w-100 px-4 pl-12 pb-3 pt-1" v-if="tarifa.preciosList && tarifa.preciosList.length > 0">
                  <div v-for="(p, i) in tarifa.preciosList" :key="i" class="d-flex align-center justify-space-between mb-2 rounded px-3 py-2" style="background-color: #f8fafc; border: 1px solid #f1f5f9;">
                    <div class="text-caption grey--text text--darken-2 d-flex flex-column flex-sm-row align-start align-sm-center">
                      <span class="font-weight-bold mr-3 mb-1 mb-sm-0" style="color: #334155;">{{ p.modName }}</span>
                      <span class="d-flex align-center">
                        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                        {{ p.horName }}
                      </span>
                    </div>
                    <v-text-field v-model="p.precio" dense outlined hide-details prefix="$" :suffix="p.moneda"
                      style="max-width: 140px; background-color: white;"></v-text-field>
                  </div>
                </div>
              </v-list-item>
            </v-list-group>
            <v-divider v-if="index < mockTarifarios.length - 1" :key="'div-' + index"></v-divider>
          </template>
        </v-list>
      </v-card>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <v-avatar color="grey lighten-4" size="80" class="mb-4">
        <v-icon size="40" color="grey lighten-1">mdi-file-compare</v-icon>
      </v-avatar>
      <h3 class="text-h6 grey--text text--darken-2 mb-2">Esperando temporada de origen</h3>
      <p class="grey--text max-w-sm mx-auto">Selecciona una temporada de origen en el paso 1 para ver el listado de
        tarifas
        que puedes replicar.</p>
    </div>

    <!-- Botones de Acción -->
    <div v-if="isReplicating" class="px-4 py-4 w-100">
      <div class="d-flex justify-space-between text-caption mb-1">
        <span>Copiando tarifas...</span>
        <span>{{ Math.round(replicateProgress) }}%</span>
      </div>
      <v-progress-linear :value="replicateProgress" color="primary" height="8" rounded></v-progress-linear>
    </div>
    <v-card-actions v-else class="px-0 pt-6 d-flex justify-end"
      style="border-top: 1px solid #e2e8f0; margin-top: 20px;">
      <v-btn text color="grey darken-1" class="mr-2 text-none" @click="$emit('cancel')">Cancelar</v-btn>
      <v-btn color="primary" depressed class="text-none font-weight-bold px-6" :disabled="!isReadyToReplicate"
        @click="replicateSelected">
        <v-icon left>mdi-content-copy</v-icon> Replicar {{ selectedTarifas.length }} tarifas
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import TarifasClass from "../../../clases/Tarifa.js";

export default {
  name: "ReplicarTarifasMasivo",
  props: {
    temporadas: { type: Array, default: () => [] },
    tarifarios: { type: Array, default: () => [] },
    subcategorias: { type: Array, default: () => [] },
    tiposJuego: { type: Array, default: () => [] },
    horarios: { type: Array, default: () => [] }
  },
  data() {
    return {
      temporadaOrigen: null,
      temporadaDestino: null,
      selectedTarifas: [],
      selectAll: false,
      isReplicating: false,
      replicateProgress: 0,

      mockTarifarios: []
    };
  },
  computed: {
    temporadasList() {
      // Excluir "ALL" u opciones generales
      return this.temporadas.filter(t => t.id !== 'ALL');
    },
    isReadyToReplicate() {
      return this.temporadaOrigen &&
        this.temporadaDestino &&
        this.temporadaOrigen !== this.temporadaDestino &&
        this.selectedTarifas.length > 0;
    },
    allTarifasIds() {
      // Extrae todos los IDs de las tarifas para la funcionalidad de 'Seleccionar Todo'
      return this.mockTarifarios.reduce((acc, tarifario) => {
        const ids = tarifario.tarifas.map(t => t.id);
        return acc.concat(ids);
      }, []);
    }
  },
  methods: {
    async onOrigenChange() {
      this.selectedTarifas = [];
      this.selectAll = false;
      this.mockTarifarios = [];

      let depto = localStorage.getItem("selectedDepartment");
      let originTarifas = await TarifasClass.getMixTarifasByDep(depto, this.temporadaOrigen);

      let grouped = {};
      for (let t of originTarifas) {
        let tId = t.Tarifario;
        if (!grouped[tId]) {
          let tarifarioData = this.tarifarios.find(x => x.value == tId);
          grouped[tId] = {
            id: tId,
            nombre: tarifarioData ? tarifarioData.text : "Tarifario " + tId,
            tarifas: []
          };
        }

        let subcatName = "Tarifa";
        if (t.Subcategoria && t.Subcategoria.length > 0) {
          let sc = this.subcategorias.find(x => x.id === t.Subcategoria[0]);
          if (sc) subcatName = sc.text;
        }

        let preciosList = [];
        if (t.arrayPrecios) {
          t.arrayPrecios.forEach(mod => {
            let tj = this.tiposJuego.find(x => x.id === mod.id);
            let modName = tj ? tj.nombre : mod.id;
            
            if (mod.horarios) {
              mod.horarios.forEach(hor => {
                let hj = this.horarios.find(x => x.id === hor.id);
                let horName = hj ? `${hj.HoraInicio.substring(0, 5)} - ${hj.HoraTermino.substring(0, 5)}` : hor.id;
                
                preciosList.push({
                  dias: mod.dias ? mod.dias.join("-") : "",
                  modId: mod.id,
                  horId: hor.id,
                  modName: modName,
                  horName: horName,
                  precio: hor.PcoVen || 0,
                  moneda: hor.Moneda || 'USD'
                });
              });
            }
          });
        }

        grouped[tId].tarifas.push({
          id: t.Id || t.id,
          nombre: subcatName,
          preciosList: preciosList,
          originalDoc: t
        });
      }
      this.mockTarifarios = Object.values(grouped);
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedTarifas = [];
      } else {
        this.selectedTarifas = [...this.allTarifasIds];
      }
      this.selectAll = !this.selectAll;
    },
    isTarifarioSelected(tarifario) {
      if (!tarifario.tarifas || tarifario.tarifas.length === 0) return false;
      // Devuelve true si TODAS las tarifas de este tarifario están seleccionadas
      return tarifario.tarifas.every(t => this.selectedTarifas.includes(t.id));
    },
    isTarifarioIndeterminate(tarifario) {
      if (!tarifario.tarifas || tarifario.tarifas.length === 0) return false;
      // Devuelve true si ALGUNAS (pero no todas) las tarifas están seleccionadas
      const selectedCount = tarifario.tarifas.filter(t => this.selectedTarifas.includes(t.id)).length;
      return selectedCount > 0 && selectedCount < tarifario.tarifas.length;
    },
    toggleTarifario(tarifario) {
      const allSelected = this.isTarifarioSelected(tarifario);
      const tarifaIds = tarifario.tarifas.map(t => t.id);

      if (allSelected) {
        // Deseleccionar todas las tarifas de este tarifario
        this.selectedTarifas = this.selectedTarifas.filter(id => !tarifaIds.includes(id));
      } else {
        // Seleccionar todas las tarifas de este tarifario (agregando las que falten)
        const newSelection = [...this.selectedTarifas];
        for (const id of tarifaIds) {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        }
        this.selectedTarifas = newSelection;
      }
    },
    async replicateSelected() {
      this.isReplicating = true;
      this.replicateProgress = 0;
      let total = this.selectedTarifas.length;
      let current = 0;

      let selectedTarifaObjs = [];
      for (let group of this.mockTarifarios) {
        for (let t of group.tarifas) {
          if (this.selectedTarifas.includes(t.id)) {
            selectedTarifaObjs.push(t);
          }
        }
      }

      for (let tarifa of selectedTarifaObjs) {
        let doc = JSON.parse(JSON.stringify(tarifa.originalDoc));

        doc.Temporada = this.temporadaDestino;
        delete doc.Id;
        delete doc.arrayPrecios;
        delete doc.temporadaNombre;
        delete doc.SubcategoriaData;
        delete doc.data;

        for (let p of tarifa.preciosList) {
          let newPrice = parseFloat(p.precio);
          if (!isNaN(newPrice) && doc.Precios && doc.Precios[p.dias] && doc.Precios[p.dias][p.modId] && doc.Precios[p.dias][p.modId][p.horId]) {
            doc.Precios[p.dias][p.modId][p.horId].PcoVen = newPrice;
          }
        }

        try {
          await this.$store.getters.getFirebaseApp.fba.call("RVA-Tarifas-createTarifa", doc);
        } catch (err) {
          console.error("Error copiando tarifa:", err);
        }

        current++;
        this.replicateProgress = (current / total) * 100;
      }

      this.$swal("Completado", "Las tarifas se replicaron exitosamente.", "success");
      this.isReplicating = false;
      this.$emit('cancel');
    }
  },
  watch: {
    selectedTarifas(newVal) {
      if (newVal.length === this.allTarifasIds.length && this.allTarifasIds.length > 0) {
        this.selectAll = true;
      } else {
        this.selectAll = false;
      }
    }
  }
};
</script>

<style scoped>
/* Ajustes extra si son necesarios */
.bg-transparent {
  background-color: transparent !important;
}
</style>
